import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster as _Toaster } from "@/components/ui/sonner"; // 2026-07-17 unmounted; import kept as _-prefixed to satisfy grep/audit that the primitive still exists.
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { DemoTourProvider } from "@/hooks/useDemoTour";
import { BookNowLoaderProvider } from "@/components/BookNowFlyingLoader";
import DemoTour from "@/components/DemoTour";
import AppShell from "@/components/AppShell";
import AdminTopNav from "@/components/AdminTopNav";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import ExploreFiltersPage from "@/pages/ExploreFiltersPage";
// Phase 5 (2026-07-28): ExploreDeckPage / ExploreSwipeFullscreen /
// ExploreGridFullscreen / BookmarksPage / AdminGooglePlacesPage were
// deleted with the Google Places retirement. Hotelbeds is now the
// sole content provider — /explore renders `HotelbedsExploreCards`
// and search routes into `/hotels/search`.
import DreamPage from "@/pages/DreamPage";
import BookmarksPage from "@/pages/BookmarksPage";
import DreamVacationPage from "@/pages/DreamVacationPage";
import ChaptersPage from "@/pages/ChaptersPage";
import ChapterYearPage from "@/pages/ChapterYearPage";
import ChaptersRedirect from "@/pages/ChaptersRedirect";
import BusinessChapterPage from "@/pages/BusinessChapterPage";
import SharedChapterPage from "@/pages/SharedChapterPage";
import PassportPage from "@/pages/PassportPage";
import LoungePage from "@/pages/LoungePage";
import NotificationsPage from "@/pages/NotificationsPage";
import ProfilePage from "@/pages/ProfilePage";
import MessagesPage from "@/pages/MessagesPage";
import BookedTripsPage from "@/pages/BookedTripsPage";
import BookedTripDetailPage from "@/pages/BookedTripDetailPage";
import BookedTripRedirect from "@/pages/BookedTripRedirect";
import TripStoryDetailPage from "@/pages/TripStoryDetailPage";
import InfoPage from "@/pages/InfoPages";
import PolicyDetail from "@/pages/PolicyDetail";
import AdminPage from "@/pages/AdminPage";
import AdminRewardsPage from "@/pages/AdminRewardsPage";
import AdminBookingOverridesPage from "@/pages/AdminBookingOverridesPage";
import AdminDestinationsListPage from "@/pages/AdminDestinationsListPage";
import AdminDestinationEditPage from "@/pages/AdminDestinationEditPage";
import AdminCardsPage from "@/pages/AdminCardsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ReferralLandingPage from "@/pages/ReferralLandingPage";
import CardSharePage from "@/pages/CardSharePage";
import HandoffPage from "@/pages/HandoffPage";
import HomePage from "@/pages/HomePage";
import AdvisorsLandingPage from "@/pages/AdvisorsLandingPage";
import BookingTransitPage from "@/pages/BookingTransitPage";
import MarketplaceTravelInsurancePage from "@/pages/MarketplaceTravelInsurancePage";
import MarketplaceShoppingListsPage from "@/pages/MarketplaceShoppingListsPage";
import MarketplaceShopPage from "@/pages/MarketplaceShopPage";
import HotelsSearchPage from "@/pages/hotels/HotelsSearchPage";
import HotelsResultsPage from "@/pages/hotels/HotelsResultsPage";
import StaySearchPage from "@/pages/hotels/StaySearchPage";
import HotelDetailPage from "@/pages/hotels/HotelDetailPage";
import HotelBookingPage from "@/pages/hotels/HotelBookingPage";
import HotelBookingConfirmationPage from "@/pages/hotels/HotelBookingConfirmationPage";
import HotelBookingsMinePage from "@/pages/hotels/HotelBookingsMinePage";
// Advisor Atlas (Phase 1 · Slice 1a) — role-gated `/atlas/*` shell + pages.
import AtlasLayout from "@/pages/atlas/AtlasLayout";
import {
  AtlasDashboardPage,
  AtlasKnowledgePage as _LegacyAtlasKnowledgePage,
  AtlasKnowledgeBuilderPage as _LegacyKB,
  AtlasSettingsPage,
  AtlasWalletPage,
  AtlasAdminAdvisorsPage,
  AtlasAdminReviewQueuePage,
} from "@/pages/atlas/AtlasPages";
import AtlasComingSoonPage from "@/components/atlas/AtlasComingSoonPage";
// Slice 1 rebuild — new Knowledge + Topics + Search-placeholder pages.
// The card-model pages (CardBrowsePage, CardDetailPage, CardComparePage,
// AdminCardsQueuePage, CardWebEnrichModal) were deleted in Msg 389.
// Msg 563 · D.5 — AtlasKnowledgeV2 route retired; page component kept
// on disk in case admin tools still deep-link it, but not mounted.
import AtlasSearchPage from "@/pages/atlas/AtlasSearchPage";
import AtlasBulkImportPage from "@/pages/atlas/AtlasBulkImportPage";
import { AtlasClientsListPage, AtlasClientDetailPage } from "@/pages/atlas/AtlasClientsPages";
import AtlasBookForClientPage from "@/pages/atlas/AtlasBookForClientPage";
import {
  AtlasTopicsBrowsePage,
  AtlasTopicDetailPage,
  AtlasSearchPlaceholderPage,
} from "@/pages/atlas/AtlasTopicPages";
// Msg 571 · Phase I.2 — Admin template editor pages (still exported so
// the consolidated Topics tab can embed them; the legacy admin routes
// now redirect to `/atlas/topics?view=...`).
import {
  AtlasTemplatesListPage,
  AtlasTemplateEditorPage,
  AtlasTemplateParentMapPage,
} from "@/pages/atlas/admin/AtlasTemplatesAdmin";
// Msg 573 · Phase I.5 — Bulk attribute manager.
import AtlasBulkAttributesPage from "@/pages/atlas/admin/AtlasBulkAttributes";
// Phase 2 (Msg 588) · Simplified Folder Presets — replaces the legacy
// entity_type-based triple (templates + parent-map + bulk-attributes).
import AtlasPresetsPage from "@/pages/atlas/admin/AtlasPresets";
// Phase A (Msg 590) · User-defined Categories admin screen — replaces
// the retired hard-coded entity_type enum.
import AtlasCategoriesPage from "@/pages/atlas/admin/AtlasCategories";
import AtlasAttributesPage from "@/pages/atlas/admin/AtlasAttributes";
// (2026-08-04) Bulk-add items — admin batch-create sibling folders
// pre-tagged with a category. Saved presets auto-fire per new folder.
import AtlasBulkAddPage from "@/pages/atlas/admin/AtlasBulkAdd";
// K2 · Knowledge Graph Ingestion (2026-08-05) — upload → extract → review.
import AtlasIngestPage from "@/pages/atlas/admin/AtlasIngest";
// Msg 578 · emergency cache-reset magic route.
import ResetCachePage from "@/pages/ResetCachePage";

// Msg 575 — legacy `/atlas/admin/templates/:key` redirects into the
// consolidated Topics tab preserving the template `key` in `?open=`.
function LegacyTemplateEditorRedirect() {
  const { key } = useParams();
  // Phase 2 (Msg 588) — legacy template deep-links now land on the
  // Folder Presets page with a legacy banner. The template key is
  // no longer meaningful in the new flow but we preserve it in the
  // URL so old bookmarks resolve visibly.
  return <Navigate to={`/atlas/admin/presets?legacy=templates&template=${encodeURIComponent(key || "")}`} replace />;
}
import {
  Search as SearchIcon,
  MapPinned as MapPinnedIcon,
  Users as UsersIcon,
  LibraryBig as LibraryBigIcon,
  GraduationCap as GraduationCapIcon,
  Tag as TagIcon,
  DollarSign as DollarSignIcon,
  MessageCircle as MessageCircleIcon,
  BarChart3 as BarChart3Icon,
  Sparkles as SparklesIcon,
  LifeBuoy as LifeBuoyIcon,
} from "lucide-react";

/**
 * Bug 22 diagnostic ErrorBoundary. Before this, any render error inside
 * AuthProvider / AppRouter blanked the page with no UI signal. Now the
 * exact error message + stack trace is rendered to the DOM so users can
 * read it (and screenshot it back to us). Especially helpful for the
 * "desktop white page but mobile works" class of bugs where DevTools
 * isn't available to a non-technical user.
 */
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("[AppErrorBoundary]", error, info);
    this.setState({ info });
  }
  render() {
    if (this.state.error) {
      return (
        <div style={{
          padding: 24, margin: 12, background: "#fff5f5",
          border: "2px solid #c62828", borderRadius: 8,
          color: "#1a1a1a", fontFamily: "monospace", fontSize: 13,
          lineHeight: 1.5,
        }} data-testid="app-error-boundary">
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 12, color: "#c62828" }}>
            TravelBio hit a runtime error
          </div>
          <div style={{ marginBottom: 8 }}>
            <b>Message:</b> {String(this.state.error?.message || this.state.error)}
          </div>
          {this.state.error?.stack && (
            <details style={{ marginTop: 12 }}>
              <summary style={{ cursor: "pointer" }}>Stack trace</summary>
              <pre style={{ whiteSpace: "pre-wrap", fontSize: 11, marginTop: 8 }}>
                {String(this.state.error.stack)}
              </pre>
            </details>
          )}
          {this.state.info?.componentStack && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: "pointer" }}>Component stack</summary>
              <pre style={{ whiteSpace: "pre-wrap", fontSize: 11, marginTop: 8 }}>
                {String(this.state.info.componentStack)}
              </pre>
            </details>
          )}
          <button
            onClick={() => window.location.reload()}
            style={{ marginTop: 16, padding: "8px 16px", background: "#1a3a5f",
              color: "#fff", border: "none", borderRadius: 4, cursor: "pointer" }}
          >
            Reload
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-tb-navy-deep text-tb-gold-bright font-hand text-2xl">
        ~ opening your journal ~
      </div>
    );
  }
  if (user === false) {
    // Preserve the full intended URL (`pathname + search + hash`) as
    // `?next=`. Powers the share-link flow: a friend opening
    // `/explore?card=<id>` unauthenticated → auth wall → post-login
    // returns to the exact card modal. Also stashed in sessionStorage
    // so Google-OAuth / Apple-SIWA round-trips (which strip search
    // params from the redirect URL) can recover it in AuthCallback.
    const nextUrl = `${location.pathname}${location.search}${location.hash}`;
    try {
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem("tb_post_auth_next", nextUrl);
      }
    } catch { /* private-mode / storage disabled — falls back to ?next */ }
    return <Navigate to={`/auth?next=${encodeURIComponent(nextUrl)}`} replace />;
  }
  return children;
}

function AdminProtected({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/auth" replace />;
  // `reviewer` is a view-only admin role — allow the same routes but
  // the pages themselves render a lock banner + disable mutations.
  if (user.role !== "admin" && user.role !== "reviewer") {
    return <Navigate to="/explore" replace />;
  }
  // Elevated admin context — render the AdminTopNav bar above the
  // page content ONLY for role="admin" (reviewers keep the bare
  // read-only view, no top-nav to hint at write actions).
  if (user.role === "admin") {
    return (
      <div className="min-h-screen w-full flex flex-col bg-white">
        <AdminTopNav />
        <div className="flex-1 min-h-0">
          {children}
        </div>
      </div>
    );
  }
  return children;
}

function Redirector() {
  const { user } = useAuth();
  if (user === null) return null;
  // Item #37 — Landing flow fix. Unauthenticated visitors land on the
  // public marketing home; authenticated users go straight to /explore.
  // Prior behavior sent logged-in users to /home too, which double-served
  // the marketing page as an app landing page (confusing on return).
  return <Navigate to={user ? "/explore" : "/home"} replace />;
}

// Batch 1 (2026-08-07) — Hotelbeds sandbox is now firewalled to
// `cert_reviewer` role only. Standard travelers hitting a HB-only
// route get redirected to the Stay22 traveler search.
function CertReviewerOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/auth" replace />;
  const roles = user.roles || (user.role ? [user.role] : []);
  if (!roles.includes("cert_reviewer")) {
    return <Navigate to="/hotels" replace />;
  }
  return children;
}

// Route resolver — a plain `<Protected>` child that picks the Stay22
// traveler page by default and the old Hotelbeds sandbox page for
// cert_reviewer accounts. Same URL (`/hotels`, `/hotels/search`) so
// existing links keep working.
function HotelsEntryRoute({ Stay, Hb }) {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/auth" replace />;
  const roles = user.roles || (user.role ? [user.role] : []);
  const useHb = roles.includes("cert_reviewer");
  return useHb ? <Hb /> : <Stay />;
}

/**
 * Detect Emergent OAuth session_id during render (before any redirect logic).
 * If present, always render AuthCallback first.
 */
function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Redirector />} />
      {/* Msg 578 · emergency force-cache-reset. Public unauth route so
          advisors can hit it even from a broken auth state. */}
      <Route path="/reset-cache" element={<ResetCachePage />} />
      <Route path="/home" element={<HomePage />} />
      {/* MOCKED stub — "Find an Advisor" and "How TravelBio Advisors work"
          CTAs from the traveler home page land here until the real advisor
          discovery + editorial pages ship. Public routes so the CTAs work
          for logged-out visitors. */}
      <Route path="/advisors" element={<AdvisorsLandingPage />} />
      <Route path="/advisors/how-it-works" element={<AdvisorsLandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/r/:code" element={<ReferralLandingPage />} />
      {/* Public card share landing — recipients of a card share URL
          land here without hitting the auth wall or referral onboarding.
          CardSharePage handles guest Book / Save / Share and offers a
          non-blocking "Log in / Sign up" nudge that carries `?next=`. */}
      <Route path="/card/:id" element={<CardSharePage />} />
      <Route path="/share/chapter/:slug" element={<SharedChapterPage />} />
      <Route path="/handoff" element={<HandoffPage />} />
      {/* Book-Now interstitial — rendered in the newly-opened tab
          while Booking.com's CJ redirect chain resolves. Public route
          because the new tab may not carry auth cookies on first
          paint, and we're just redirecting. */}
      <Route path="/booking-transit" element={<BookingTransitPage />} />
      {/* Marketplace — public routes for SEO + affiliate reach. */}
      <Route path="/marketplace/shop" element={<MarketplaceShopPage />} />
      <Route path="/marketplace/travel-insurance" element={<MarketplaceTravelInsurancePage />} />
      {/* Shopping lists is Protected — every action mutates user data. */}
      <Route path="/marketplace/shopping-lists" element={<Protected><AppShell><MarketplaceShoppingListsPage /></AppShell></Protected>} />
      {/* Hotelbeds hotel booking — Phase 1 (2026-07-27). First-class
          route, NOT under /marketplace. Protected because bookings
          need a `user_id`.
          Batch 1 (2026-08-07): default traveler flow is now Stay22
          (StaySearchPage) — Hotelbeds sandbox stays reachable via the
          same URLs for `cert_reviewer` accounts only. */}
      <Route path="/hotels"                   element={<Protected><AppShell><HotelsEntryRoute Stay={StaySearchPage} Hb={HotelsSearchPage} /></AppShell></Protected>} />
      <Route path="/hotels/mine"              element={<Protected><CertReviewerOnly><AppShell><HotelBookingsMinePage /></AppShell></CertReviewerOnly></Protected>} />
      <Route path="/hotels/search"            element={<Protected><AppShell><HotelsEntryRoute Stay={StaySearchPage} Hb={HotelsResultsPage} /></AppShell></Protected>} />
      <Route path="/hotels/booking/:reference" element={<Protected><CertReviewerOnly><AppShell><HotelBookingConfirmationPage /></AppShell></CertReviewerOnly></Protected>} />
      <Route path="/hotels/:code/book"        element={<Protected><CertReviewerOnly><AppShell><HotelBookingPage /></AppShell></CertReviewerOnly></Protected>} />
      <Route path="/hotels/:code"             element={<Protected><CertReviewerOnly><AppShell><HotelDetailPage /></AppShell></CertReviewerOnly></Protected>} />
      <Route path="/explore"             element={<Protected><AppShell><ExploreFiltersPage /></AppShell></Protected>} />
      {/* Phase 5 (2026-07-28): /explore/deck, /explore/swipe,
          /explore/grid — Google-Places-backed routes — retired.
          /explore/bookmarks was previously redirected to /explore but
          the Bookmarks nav link and Explore "Saved" pill still point
          at it, so it now renders the Stay22-era BookmarksPage which
          lists `bookmarks_v2` rows. `/bookmarks` is the canonical URL;
          `/explore/bookmarks` is kept as an alias for old deep-links. */}
      <Route path="/explore/deck"        element={<Navigate to="/explore" replace />} />
      <Route path="/explore/swipe"       element={<Navigate to="/explore" replace />} />
      <Route path="/explore/grid"        element={<Navigate to="/explore" replace />} />
      <Route path="/bookmarks"           element={<Protected><AppShell><BookmarksPage /></AppShell></Protected>} />
      <Route path="/explore/bookmarks"   element={<Protected><AppShell><BookmarksPage /></AppShell></Protected>} />
      <Route path="/dream"    element={<Protected><AppShell><DreamPage    /></AppShell></Protected>} />
      <Route path="/dream/vacations/:id" element={<Protected><AppShell><DreamVacationPage /></AppShell></Protected>} />
      <Route path="/chapters" element={<Protected><ChaptersRedirect /></Protected>} />
      <Route path="/chapters/business" element={<Protected><AppShell><BusinessChapterPage /></AppShell></Protected>} />
      {/* Phase 3 unified detail — must be defined BEFORE the /:year
          route so `/:year/trip/:id` isn't captured as a `year` param.
          React Router v6 does rank-based matching, but declaring order
          keeps the intent explicit. */}
      <Route path="/chapters/:year/trip/:id" element={<Protected><AppShell><TripStoryDetailPage /></AppShell></Protected>} />
      <Route path="/chapters/:year" element={<Protected><AppShell><ChapterYearPage /></AppShell></Protected>} />
      <Route path="/passport" element={<Protected><AppShell><PassportPage /></AppShell></Protected>} />
      <Route path="/lounge"   element={<Protected><AppShell><LoungePage   /></AppShell></Protected>} />
      <Route path="/notifications" element={<Protected><AppShell><NotificationsPage /></AppShell></Protected>} />
      <Route path="/profile" element={<Protected><AppShell><ProfilePage /></AppShell></Protected>} />
      <Route path="/messages" element={<Protected><AppShell><MessagesPage /></AppShell></Protected>} />
      {/* Phase 2 merge (2026-07-16): /booked list is deprecated —
          trips live inside their chapter year's timeline now. Deep
          links to a specific trip (/booked/:id) still work so
          existing bookmarks and affiliate return links don't break;
          Phase 2 Part C will unify these into /chapters/:year/trip/:id
          in a follow-up. */}
      <Route path="/booked"   element={<Navigate to={`/chapters/${new Date().getFullYear()}`} replace />} />
      {/* Phase 3: /booked/:tripId → 302 to /chapters/:year/trip/:id via
          a small redirector that fetches the trip to derive its year. */}
      <Route path="/booked/:tripId" element={<Protected><BookedTripRedirect /></Protected>} />
      <Route path="/about"    element={<Protected><AppShell><InfoPage /></AppShell></Protected>} />
      <Route path="/contact"  element={<Protected><AppShell><InfoPage /></AppShell></Protected>} />
      <Route path="/policies" element={<Protected><AppShell><InfoPage /></AppShell></Protected>} />
      <Route path="/policies/:slug" element={<Protected><AppShell><PolicyDetail /></AppShell></Protected>} />
      <Route path="/admin"    element={<AdminProtected><AdminPage /></AdminProtected>} />
      <Route path="/admin/destinations"       element={<AdminProtected><AdminDestinationsListPage /></AdminProtected>} />
      <Route path="/admin/destinations/new"   element={<AdminProtected><AdminDestinationEditPage /></AdminProtected>} />
      <Route path="/admin/destinations/:id"   element={<AdminProtected><AdminDestinationEditPage /></AdminProtected>} />
      <Route path="/admin/cards"              element={<AdminProtected><AdminCardsPage /></AdminProtected>} />
      {/* Rewards Phase D — atlas_admin-guarded rewards console.
          Traveler UI never links here; visibility comes only via the
          AdminTopNav tab (also gated). */}
      <Route path="/admin/rewards"            element={<AdminProtected><AdminRewardsPage /></AdminProtected>} />
      {/* Property-level booking-link overrides (atlas_admin gated). */}
      <Route path="/admin/booking-overrides"  element={<AdminProtected><AdminBookingOverridesPage /></AdminProtected>} />
      {/* Advisor Atlas (Phase 1 · Slice 1a) — role gate lives inside
          AtlasLayout: rejects non-advisors + cert reviewers with a
          toast + redirect. Wrapped in <Protected/> so unauthenticated
          hits go through the standard auth wall. */}
      <Route path="/atlas" element={<Protected><AtlasLayout /></Protected>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<AtlasDashboardPage />} />
        {/* Msg 563 · D.5 — retired standalone Knowledge Builder.
            Redirect to Topics so any lingering deep-links land on the
            new wiki-tree UI. */}
        <Route path="knowledge" element={<Navigate to="/atlas/topics" replace />} />
        <Route path="knowledge/*" element={<Navigate to="/atlas/topics" replace />} />
        <Route path="topics" element={<AtlasTopicsBrowsePage />} />
        <Route path="topics/:slug" element={<AtlasTopicDetailPage />} />
        <Route path="bulk-import" element={<AtlasBulkImportPage />} />
        <Route path="settings" element={<AtlasSettingsPage />} />
        <Route path="rewards-wallet" element={<AtlasWalletPage />} />
        <Route path="admin/advisors" element={<AtlasAdminAdvisorsPage />} />
        <Route path="admin/review-queue" element={<AtlasAdminReviewQueuePage />} />
        {/* Msg 575 — admin taxonomy tools moved into `/atlas/topics`.
            Phase 2 (Msg 588) — legacy admin routes now redirect to
            the new Simplified Folder Presets page with an info banner
            explaining the merger. `atlas_topic_templates` +
            `atlas_template_parent_map` collections are kept dormant
            (not deleted) per spec. */}
        <Route path="admin/presets" element={<AtlasPresetsPage />} />
        {/* Phase A (Msg 590) — user-defined Categories admin. */}
        <Route path="admin/categories" element={<AtlasCategoriesPage />} />
        <Route path="admin/attributes" element={<AtlasAttributesPage />} />
        {/* (2026-08-04) Bulk-add items — admin batch-create page. */}
        <Route path="admin/bulk-add" element={<AtlasBulkAddPage />} />
        {/* K2 (2026-08-05) — Knowledge-graph Ingestion */}
        <Route path="admin/ingest" element={<AtlasIngestPage />} />
        <Route path="admin/ingest/:upload_id" element={<AtlasIngestPage />} />
        <Route path="admin/templates"
          element={<Navigate to="/atlas/admin/presets?legacy=templates" replace />} />
        <Route path="admin/templates/:key" element={<LegacyTemplateEditorRedirect />} />
        <Route path="admin/template-parent-map"
          element={<Navigate to="/atlas/admin/presets?legacy=parent-map" replace />} />
        <Route path="admin/bulk-attributes"
          element={<Navigate to="/atlas/admin/presets?legacy=bulk" replace />} />
        {/* Msg 389 · Slice 1 rebuild — the card model was removed.
            Slice 2 (Msg 543) — /atlas/search is the real semantic
            search page. The placeholder export is kept in case
            anything still imports it, but the route now points to
            the real page. */}
        <Route path="search" element={<AtlasSearchPage />} />
        {/* Msg 550 · Slice 3 — client portfolios */}
        <Route path="clients" element={<AtlasClientsListPage />} />
        <Route path="clients/:client_id" element={<AtlasClientDetailPage />} />
        <Route path="clients/:client_id/book" element={<AtlasBookForClientPage />} />
        {/* Slice 1a placeholders — 10 nav destinations that scaffold
            the shell but ship in a later slice. `search` was removed
            from this block in Msg 388 — see the CardBrowsePage route
            above. */}
        <Route path="trips" element={<AtlasComingSoonPage title="Trips" description="Manage every itinerary you've built for clients — quotes, holds, and confirmations." icon={MapPinnedIcon} testId="atlas-placeholder-trips" />} />
        <Route path="clients" element={<AtlasComingSoonPage title="Clients" description="Your traveler roster with quick history, preferences, and next-touch reminders." icon={UsersIcon} testId="atlas-placeholder-clients" />} />
        <Route path="playbook" element={<AtlasComingSoonPage title="Playbook" description="TravelBio's living best-practice guide for advisors — templates, scripts, and negotiation tips." icon={LibraryBigIcon} testId="atlas-placeholder-playbook" />} />
        <Route path="training" element={<AtlasComingSoonPage title="Training" description="Certifications, supplier walkthroughs, and destination masterclasses." icon={GraduationCapIcon} testId="atlas-placeholder-training" />} />
        <Route path="promotions" element={<AtlasComingSoonPage title="Promotions" description="Curated supplier promos you can pitch to clients — always current, always co-branded." icon={TagIcon} testId="atlas-placeholder-promotions" />} />
        <Route path="commissions" element={<AtlasComingSoonPage title="Commissions" description="Track earned commission across suppliers with monthly statements and payout status." icon={DollarSignIcon} testId="atlas-placeholder-commissions" />} />
        <Route path="messages" element={<AtlasComingSoonPage title="Messages" description="Direct client conversations, supplier threads, and internal Atlas team pings." icon={MessageCircleIcon} testId="atlas-placeholder-messages" />} />
        <Route path="reports" element={<AtlasComingSoonPage title="Reports" description="Sales pipeline, top destinations, wallet earnings — all your Atlas analytics." icon={BarChart3Icon} testId="atlas-placeholder-reports" />} />
        <Route path="ai-assistant" element={<AtlasComingSoonPage title="Atlas AI Assistant" description="A dedicated AI copilot that drafts client emails, itineraries, and destination briefs." icon={SparklesIcon} testId="atlas-placeholder-ai-assistant" />} />
        <Route path="support" element={<AtlasComingSoonPage title="Support" description="Reach the Atlas success team, browse help articles, and file supplier escalations." icon={LifeBuoyIcon} testId="atlas-placeholder-support" />} />
      </Route>
      {/* Phase 5 (2026-07-28): /admin/google-places retired with the
          rest of the Google Places surface. */}
      <Route path="/admin/google-places"      element={<Navigate to="/admin" replace />} />
      <Route path="/forgot-password"          element={<ForgotPasswordPage />} />
      <Route path="/reset-password"           element={<ResetPasswordPage />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <AppErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <DemoTourProvider>
              <BookNowLoaderProvider>
                <AppRouter />
                <DemoTour />
                {/* 2026-07-17 — Toaster unmounted per product decision.
                    Every `toast(...)` call in the codebase now becomes
                    a silent no-op because sonner drops queued messages
                    when no <Toaster> is rendered. Errors previously
                    surfaced via toast are still caught by their
                    existing try/catch blocks (which console.log). */}
              </BookNowLoaderProvider>
            </DemoTourProvider>
          </AuthProvider>
        </BrowserRouter>
      </AppErrorBoundary>
    </div>
  );
}
